import { roundTo } from '../chart-shared';
import type { HistogramConfig } from '../histogram/histogram-chart.model';
import { computeHistogram } from '../histogram/histogram-chart.util';

export interface ComputedOgive {
  /** [upperBoundary, cumulativeValue] pairs — first point is (lowerBoundary, 0) */
  readonly points: Array<[number, number]>;
  readonly xMin: number;
  readonly xMax: number;
  readonly yMax: number;
}

export function computeOgive(data: number[], config: HistogramConfig, percentage: boolean): ComputedOgive {
  if (data.length === 0) {
    return { points: [], xMin: 0, xMax: 0, yMax: 0 };
  }

  // Always compute from raw frequency counts, regardless of histogram yAxisType
  const rawConfig: HistogramConfig = { ...config, yAxisType: 'frequency' };
  const { values, binEdges } = computeHistogram(data, rawConfig);

  if (binEdges.length < 2) {
    return { points: [], xMin: 0, xMax: 0, yMax: 0 };
  }

  const total = values.reduce((a, b) => a + b, 0);
  const points: Array<[number, number]> = [[binEdges[0], 0]];

  let cumulative = 0;
  for (let i = 0; i < values.length; i++) {
    cumulative += values[i];
    const y = percentage && total > 0 ? roundTo((cumulative / total) * 100, 2) : cumulative;
    points.push([binEdges[i + 1], y]);
  }

  const yMax = percentage && total > 0 ? 100 : cumulative;

  return {
    points,
    xMin: binEdges[0],
    xMax: binEdges[binEdges.length - 1],
    yMax,
  };
}

/**
 * Linearly interpolate the cumulative y-value on the ogive for a given x.
 * Used to draw L-shaped reading lines from y-axis → curve → x-axis.
 */
export function interpolateOgive(points: ReadonlyArray<[number, number]>, x: number): number {
  if (points.length === 0) {
    return 0;
  }
  if (x <= points[0][0]) {
    return points[0][1];
  }
  if (x >= points[points.length - 1][0]) {
    return points[points.length - 1][1];
  }

  for (let i = 1; i < points.length; i++) {
    if (x <= points[i][0]) {
      const [x0, y0] = points[i - 1];
      const [x1, y1] = points[i];
      if (x1 === x0) {
        return y0;
      }
      const t = (x - x0) / (x1 - x0);
      return roundTo(y0 + t * (y1 - y0), 4);
    }
  }

  return points[points.length - 1][1];
}

/**
 * Inverse interpolation: given a cumulative y-value, find the x on the ogive.
 * This is how students read percentiles: start from y = 25%/50%/75% on the
 * y-axis, go across to the curve, then drop down to read the x-value.
 */
export function inverseInterpolateOgive(points: ReadonlyArray<[number, number]>, targetY: number): number | null {
  if (points.length < 2) {
    return null;
  }
  if (targetY <= points[0][1]) {
    return points[0][0];
  }
  if (targetY >= points[points.length - 1][1]) {
    return points[points.length - 1][0];
  }

  for (let i = 1; i < points.length; i++) {
    if (targetY <= points[i][1]) {
      const [x0, y0] = points[i - 1];
      const [x1, y1] = points[i];
      if (y1 === y0) {
        return x0;
      }
      const t = (targetY - y0) / (y1 - y0);
      return roundTo(x0 + t * (x1 - x0), 2);
    }
  }

  return points[points.length - 1][0];
}
